import React, { useState, useEffect } from 'react';
import { Study, StudySegment, SegmentType, VisibilityLevel, MediaItem, CTAConfig, CTAType, CTATypeConfig } from '../types';
import { Icons } from './ui/Icons';
import { removeUndefined } from '../utils/objUtils';
import { AppHeader } from './ui/AppHeader';
import { AIQuotaDisplay } from './AIQuotaDisplay';
import { MediaPicker } from './MediaPicker';
import { CTAEditorModal } from './cta/CTAEditorModal';
import { generateBibleImage, generateBibleVideo, generateBibleText, ImageGenerationOptions, ImageStyle, AspectRatio, VideoStyle, VideoResolution, VideoDuration, VideoAspectRatio } from '../services/geminiService';
import { saveStudy, updateStudy, deleteStudy, uploadImageToStorage, uploadAudioToStorage, uploadVideoToStorage } from '../services/firebase';
import { generateAudio, base64ToBlob, CHIRP_VOICES, estimateCost, estimateDuration } from '../services/ttsService';
import { checkAIQuota, incrementAIQuota, getMonthlyQuotaStatus } from '../services/aiQuotaService';

import { UserProfile, Organization } from '../types';

interface StudyEditorProps {
  userUid: string;
  userName: string;
  orgId: string;
  user: UserProfile;
  organization: Organization | null;
  onClose: () => void;
  onSaveSuccess: () => void;
  onLogout: () => void;
  initialData?: Study | null;
}

// AI Generation Constants
const IMAGE_STYLES: { value: ImageStyle; label: string }[] = [
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'renaissance', label: 'Renaissance' },
  { value: 'oil_painting', label: 'Oil Painting' },
  { value: 'stained_glass', label: 'Stained Glass' },
  { value: 'ancient_mosaic', label: 'Ancient Mosaic' },
  { value: 'warm_pastoral', label: 'Warm Pastoral' },
  { value: 'illustration', label: 'Illustration' },
  { value: 'watercolor', label: 'Watercolor' },
  { value: 'sketch', label: 'Sketch' },
  { value: 'digital_art', label: 'Digital Art' },
  { value: 'anime', label: 'Anime' },
];

const ASPECT_RATIOS: { value: AspectRatio; label: string }[] = [
  { value: '16:9', label: 'Landscape (16:9)' },
  { value: '1:1', label: 'Square (1:1)' },
  { value: '9:16', label: 'Portrait (9:16)' },
  { value: '4:3', label: 'Standard (4:3)' },
  { value: '3:4', label: 'Portrait (3:4)' },
];

const VIDEO_STYLES: { value: VideoStyle; label: string }[] = [
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'animation', label: 'Animation' },
  { value: 'natural', label: 'Natural' },
  { value: 'vintage', label: 'Vintage' },
  { value: 'documentary', label: 'Documentary' },
  { value: 'dramatic', label: 'Dramatic' },
];

const VIDEO_DURATIONS: { value: VideoDuration; label: string }[] = [
  { value: 4, label: '4 seconds' },
  { value: 6, label: '6 seconds' },
  { value: 8, label: '8 seconds (Best Quality)' },
];

const VIDEO_ASPECT_RATIOS: { value: VideoAspectRatio; label: string }[] = [
  { value: '16:9', label: 'Landscape (16:9)' },
  { value: '9:16', label: 'Portrait (9:16)' },
];

const VIDEO_RESOLUTIONS: { value: VideoResolution; label: string }[] = [
  { value: '1080p', label: '1080p (Requires 8s)' },
  { value: '720p', label: '720p' },
];

// Bible books for autocomplete
const BIBLE_BOOKS = [
  // Old Testament
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy', 'Joshua', 'Judges', 'Ruth',
  '1 Samuel', '2 Samuel', '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra',
  'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs', 'Ecclesiastes', 'Song of Solomon',
  'Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel', 'Hosea', 'Joel', 'Amos',
  'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
  // New Testament
  'Matthew', 'Mark', 'Luke', 'John', 'Acts', 'Romans', '1 Corinthians', '2 Corinthians',
  'Galatians', 'Ephesians', 'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians',
  '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews', 'James', '1 Peter', '2 Peter',
  '1 John', '2 John', '3 John', 'Jude', 'Revelation'
];

const BIBLE_VERSIONS = [
  { id: 'NIV', name: 'New International Version (NIV)' },
  { id: 'KJV', name: 'King James Version (KJV)' },
  { id: 'ESV', name: 'English Standard Version (ESV)' },
  { id: 'NKJV', name: 'New King James Version (NKJV)' },
  { id: 'NLT', name: 'New Living Translation (NLT)' },
  { id: 'NASB', name: 'New American Standard Bible (NASB)' },
  { id: 'CSB', name: 'Christian Standard Bible (CSB)' },
  { id: 'MSG', name: 'The Message (MSG)' },
  { id: 'AMP', name: 'Amplified Bible (AMP)' },
  { id: 'HCSB', name: 'Holman Christian Standard Bible (HCSB)' },
  { id: 'Other', name: 'Other' },
];


export const StudyEditor: React.FC<StudyEditorProps> = ({ userUid, userName, orgId, user, organization, onClose, onSaveSuccess, onLogout, initialData }) => {
  const [activeTab, setActiveTab] = useState('browse');

  // Study ID - use existing ID or generate a new one for this editing session
  const [studyId] = useState(initialData?.id || `study_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [visibility, setVisibility] = useState<VisibilityLevel>(initialData?.visibility || VisibilityLevel.PRIVATE);
  const [tags, setTags] = useState<string[]>(initialData?.tags || []);
  const [tagInput, setTagInput] = useState('');

  // Cover Image State
  const [coverImage, setCoverImage] = useState(initialData?.coverImage || '');
  const [coverImagePosition, setCoverImagePosition] = useState<{ x: number; y: number }>(
    initialData?.coverImagePosition || { x: 50, y: 50 }
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [coverPrompt, setCoverPrompt] = useState('');
  const [isGeneratingCover, setIsGeneratingCover] = useState(false);

  const [segments, setSegments] = useState<StudySegment[]>(() => {
    if (!initialData?.segments) return [];

    // Normalize legacy segment types
    return initialData.segments.map(s => {
      // Convert 'text' to 'image-text'
      if (s.type === 'text') {
        return {
          ...s,
          type: 'image-text' as SegmentType,
          metadata: { ...s.metadata, imageUrl: '' }
        };
      }
      // Convert 'image' to 'image-text'
      if (s.type === 'image') {
        return {
          ...s,
          type: 'image-text' as SegmentType,
          content: '',
          metadata: { ...s.metadata, imageUrl: s.content }
        };
      }
      // Convert legacy 'youtube' to 'video'
      if (s.type === 'youtube' as any) {
        return {
          ...s,
          type: 'video' as SegmentType
        };
      }
      return s;
    });
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Audio generation state
  const [generatingAudioFor, setGeneratingAudioFor] = useState<string | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<string>('en-US-Studio-O');
  const [audioConfirmModal, setAudioConfirmModal] = useState<{
    show: boolean;
    segmentId: string;
    text: string;
    cost: number;
    duration: number;
    voice: string;
  } | null>(null);

  // Image generation confirmation modal
  const [imageConfirmModal, setImageConfirmModal] = useState<{
    show: boolean;
    segmentId: string;
    prompt: string;
    style: string;
    aspectRatio: string;
    credits: number;
    estimatedCost: number;
  } | null>(null);

  // Video generation confirmation modal
  const [videoConfirmModal, setVideoConfirmModal] = useState<{
    show: boolean;
    segmentId: string;
    prompt: string;
    style: string;
    duration: number;
    resolution: string;
    credits: number;
    estimatedCost: number;
  } | null>(null);

  // Media picker state for selecting reference images from library
  const [showMediaPicker, setShowMediaPicker] = useState<{
    segmentId: string;
    purpose: 'video-reference' | 'segment-image';
  } | null>(null);

  // Bible verse autocomplete state
  const [showBookSuggestions, setShowBookSuggestions] = useState<{ [segmentId: string]: boolean }>({});
  const [showSettings, setShowSettings] = useState<{ [segmentId: string]: boolean }>({});

  // AI Quota state (using monthly quotas)
  const [aiQuotaStatus, setAiQuotaStatus] = useState<{
    images: { used: number; limit: number; remaining: number };
    tts: { used: number; limit: number; remaining: number };
    videos: { used: number; limit: number; remaining: number };
    tier: string;
  } | null>(null);

  // CTA Editor state
  const [ctaEditorSegmentId, setCtaEditorSegmentId] = useState<string | null>(null);

  // Magic Text Rewrite state
  const [textMagicModal, setTextMagicModal] = useState<{
    show: boolean;
    segmentId: string;
    prompt: string;
    originalText: string;
    generatedText?: string;
  } | null>(null);
  const [isMagicGenerating, setIsMagicGenerating] = useState(false);

  // Helper to create ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Load AI quota status on mount
  useEffect(() => {
    const loadQuotaStatus = async () => {
      try {
        const status = await getMonthlyQuotaStatus(orgId);
        setAiQuotaStatus(status);
      } catch (error) {
        console.error('Failed to load AI quota status:', error);
      }
    };
    loadQuotaStatus();
  }, [orgId]);

  // Refresh quota status after AI usage
  const refreshQuotaStatus = async () => {
    try {
      const status = await getMonthlyQuotaStatus(orgId);
      setAiQuotaStatus(status);
    } catch (error) {
      console.error('Failed to refresh AI quota status:', error);
    }
  };

  const addSegment = (type: SegmentType, initialMetadata: any = {}) => {
    const newSegment: StudySegment = {
      id: generateId(),
      type,
      content: '',
      metadata: type === 'verse'
        ? { book: '', chapter: '', verses: '', version: 'NIV', ...initialMetadata }
        : type === 'image'
          ? { prompt: '', imageStyle: 'cinematic', imageAspectRatio: '16:9', ...initialMetadata }
          : type === 'image-text'
            ? { prompt: '', imageUrl: '', uiMode: 'both', imageStyle: 'cinematic', imageAspectRatio: '16:9', ...initialMetadata }
            : type === 'quiz'
              ? { quizOptions: ['', '', '', ''], correctAnswer: 0, ...initialMetadata }
              : type === 'video'
                ? { videoStyle: 'cinematic', videoResolution: '1080p', videoDuration: 8, videoAspectRatio: '16:9', ...initialMetadata }
                : { ...initialMetadata },
    };
    setSegments([...segments, newSegment]);
  };

  const updateSegment = (id: string, updates: Partial<StudySegment> | any) => {
    setSegments(segments.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const updateSegmentMetadata = (id: string, metadataUpdates: any) => {
    setSegments(segments.map(s =>
      s.id === id ? { ...s, metadata: { ...s.metadata, ...metadataUpdates } } : s
    ));
  };

  const updateQuizOption = (segmentId: string, optionIndex: number, value: string) => {
    const segment = segments.find(s => s.id === segmentId);
    if (!segment || !segment.metadata?.quizOptions) return;

    const newOptions = [...segment.metadata.quizOptions];
    newOptions[optionIndex] = value;

    updateSegmentMetadata(segmentId, { quizOptions: newOptions });
  };

  const removeSegment = (index: number) => {
    const newSegments = [...segments];
    newSegments.splice(index, 1);
    setSegments(newSegments);
  };

  const moveSegment = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === segments.length - 1)
    ) return;

    const newSegments = [...segments];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSegments[index], newSegments[targetIndex]] = [newSegments[targetIndex], newSegments[index]];
    setSegments(newSegments);
  };

  const handleAddTag = (e?: React.KeyboardEvent) => {
    if (e && e.key !== 'Enter') return;
    if (!tagInput.trim()) return;

    const newTag = tagInput.trim();
    if (!tags.includes(newTag)) {
      setTags([...tags, newTag]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleGenerateImage = async (segmentId: string) => {
    const segment = segments.find(s => s.id === segmentId);
    const prompt = segment?.metadata?.prompt;

    if (!segment || !prompt) return;

    // Check AI quota before showing confirmation
    try {
      await checkAIQuota(orgId, 'gemini-image');
    } catch (error: any) {
      alert(error.message || 'AI quota exceeded');
      return;
    }

    const style = (segment.metadata?.imageStyle as string) || 'cinematic';
    const aspectRatio = (segment.metadata?.imageAspectRatio as string) || '16:9';

    // Show confirmation modal
    setImageConfirmModal({
      show: true,
      segmentId,
      prompt,
      style,
      aspectRatio,
      credits: 1,
      estimatedCost: 0.04 // Approximate Imagen 3 cost per image
    });
  };

  const confirmGenerateImage = async () => {
    if (!imageConfirmModal) return;

    const { segmentId, prompt, style, aspectRatio } = imageConfirmModal;
    setImageConfirmModal(null);
    setIsGenerating(true);

    try {
      const options: ImageGenerationOptions = {
        prompt,
        style: style as ImageStyle,
        aspectRatio: aspectRatio as AspectRatio
      };

      const base64Image = await generateBibleImage(options);
      // Upload to Firebase Storage with organized path: orgs/{orgId}/studies/{studyId}/segments/{segmentId}-{timestamp}.png
      const timestamp = Date.now();
      const storagePath = `orgs/${orgId}/studies/${studyId}/segments/${segmentId}-${timestamp}.png`;
      const downloadURL = await uploadImageToStorage(base64Image, storagePath);

      const segment = segments.find(s => s.id === segmentId);
      if (segment?.type === 'image-text') {
        updateSegmentMetadata(segmentId, { imageUrl: downloadURL });
      } else {
        updateSegment(segmentId, { content: downloadURL });
      }

      // Increment quota after successful generation and save to media library
      await incrementAIQuota(orgId, userUid, 'gemini-image', `Image for segment ${segmentId}`, downloadURL, undefined, undefined, {
        storagePath,
        prompt,
        sourceType: 'segment',
        associatedStudyId: studyId,
        segmentId
      });
      await refreshQuotaStatus();
    } catch (error) {
      alert("Failed to generate image. Ensure API Key is valid.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageUpload = async (segmentId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert("Please upload an image file");
        return;
      }
      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event.target?.result) {
          try {
            const base64Data = event.target.result as string;
            // Upload to Firebase Storage with organized path: orgs/{orgId}/studies/{studyId}/segments/{segmentId}-{timestamp}.png
            const timestamp = Date.now();
            const storagePath = `orgs/${orgId}/studies/${studyId}/segments/${segmentId}-${timestamp}.png`;
            const downloadURL = await uploadImageToStorage(base64Data, storagePath);

            const segment = segments.find(s => s.id === segmentId);
            if (segment?.type === 'image-text') {
              updateSegmentMetadata(segmentId, { imageUrl: downloadURL });
            } else {
              updateSegment(segmentId, { content: downloadURL });
            }
          } catch (error) {
            console.error("Upload error:", error);
            alert("Failed to upload image.");
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Audio Generation Handler
  const handleGenerateAudio = async (segmentId: string, text: string) => {
    if (!text || text.trim().length === 0) {
      alert("Please enter text before generating audio.");
      return;
    }

    // Check AI quota before generating
    try {
      await checkAIQuota(orgId, 'google-tts');
    } catch (error: any) {
      alert(error.message || 'AI quota exceeded');
      return;
    }

    // Estimate cost and duration
    const cost = estimateCost(text);
    const duration = estimateDuration(text);

    // Show confirmation modal
    setAudioConfirmModal({
      show: true,
      segmentId,
      text,
      cost,
      duration,
      voice: selectedVoice
    });
  };

  const confirmGenerateAudio = async () => {
    if (!audioConfirmModal) return;

    const { segmentId, text, voice } = audioConfirmModal;
    setAudioConfirmModal(null);
    setGeneratingAudioFor(segmentId);

    try {

      // Generate audio using Google Cloud TTS
      const base64Audio = await generateAudio({
        text,
        voiceName: voice,
        speakingRate: 1.0,
        pitch: 0.0
      });

      // Convert to blob
      const audioBlob = base64ToBlob(base64Audio, 'audio/mp3');

      // Upload to Firebase Storage
      const timestamp = Date.now();
      const storagePath = `orgs/${orgId}/studies/${studyId}/audio/${segmentId}-${timestamp}.mp3`;
      const downloadURL = await uploadAudioToStorage(audioBlob, storagePath);

      // Update segment with audio URL
      updateSegment(segmentId, {
        audioUrl: downloadURL,
        audioVoice: voice,
        audioGeneratedAt: timestamp
      });

      // Increment quota after successful generation and save to media library
      await incrementAIQuota(orgId, userUid, 'google-tts', `Audio for segment ${segmentId}`, undefined, downloadURL, undefined, {
        storagePath,
        prompt: `TTS: ${text.substring(0, 100)}${text.length > 100 ? '...' : ''}`,
        sourceType: 'segment',
        associatedStudyId: studyId,
        segmentId
      });
      await refreshQuotaStatus();


    } catch (error) {
      console.error("Audio generation error:", error);
      alert("Failed to generate audio. Please check your Google Cloud API key and ensure the Text-to-Speech API is enabled.");
    } finally {
      setGeneratingAudioFor(null);
    }
  };

  const handleDeleteAudio = (segmentId: string) => {
    if (window.confirm("Delete generated audio for this segment?")) {
      updateSegment(segmentId, {
        audioUrl: undefined,
        audioVoice: undefined,
        audioGeneratedAt: undefined
      });
    }
  };

  // Cover Image Handlers
  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert("Please upload an image file");
        return;
      }
      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event.target?.result) {
          try {
            const base64Data = event.target.result as string;
            // Upload to Firebase Storage with organized path: orgs/{orgId}/studies/{studyId}/covers/{timestamp}.png
            const timestamp = Date.now();
            const storagePath = `orgs/${orgId}/studies/${studyId}/covers/${timestamp}.png`;
            const downloadURL = await uploadImageToStorage(base64Data, storagePath);
            setCoverImage(downloadURL);
          } catch (error) {
            console.error("Upload error:", error);
            alert("Failed to upload image.");
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerateCover = async () => {
    const promptToUse = coverPrompt || title;
    if (!promptToUse) {
      alert("Please enter a title or a custom prompt to generate a cover.");
      return;
    }

    // Check AI quota before generating
    try {
      await checkAIQuota(orgId, 'gemini-image');
    } catch (error: any) {
      alert(error.message || 'AI quota exceeded');
      return;
    }

    setIsGeneratingCover(true);
    try {
      const fullPrompt = promptToUse + " - biblical theme background, abstract or scenic, no text or writing";
      const base64Image = await generateBibleImage(fullPrompt);
      // Upload to Firebase Storage with organized path: orgs/{orgId}/studies/{studyId}/covers/{timestamp}.png
      const timestamp = Date.now();
      const storagePath = `orgs/${orgId}/studies/${studyId}/covers/${timestamp}.png`;
      const downloadURL = await uploadImageToStorage(base64Image, storagePath);
      setCoverImage(downloadURL);

      // Increment quota after successful generation and save to media library
      await incrementAIQuota(orgId, userUid, 'gemini-image', 'Cover image', downloadURL, undefined, undefined, {
        storagePath,
        prompt: fullPrompt,
        sourceType: 'cover',
        associatedStudyId: studyId
      });
      await refreshQuotaStatus();
    } catch (error) {
      alert("Failed to generate cover image.");
    } finally {
      setIsGeneratingCover(false);
    }
  };

  // Cover image drag handlers
  const handleCoverDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleCoverDragMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStart) return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();

    // Calculate movement as percentage of container size
    const deltaX = ((e.clientX - dragStart.x) / rect.width) * 100;
    const deltaY = ((e.clientY - dragStart.y) / rect.height) * 100;

    setCoverImagePosition(prev => ({
      x: Math.max(0, Math.min(100, prev.x + deltaX)),
      y: Math.max(0, Math.min(100, prev.y + deltaY))
    }));

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleCoverDragEnd = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleGenerateVideo = async (segmentId: string) => {
    const segment = segments.find(s => s.id === segmentId);
    if (!segment || !segment.metadata?.prompt) {
      alert("Please enter a prompt");
      return;
    }

    try {
      await checkAIQuota(orgId, 'veo-video');
    } catch (error: any) {
      alert(error.message || 'AI quota exceeded');
      return;
    }

    const style = (segment.metadata.videoStyle as string) || 'cinematic';
    const duration = (segment.metadata.videoDuration as number) || 8;
    const resolution = (segment.metadata.videoResolution as string) || '1080p';

    // Show confirmation modal
    setVideoConfirmModal({
      show: true,
      segmentId,
      prompt: segment.metadata.prompt,
      style,
      duration,
      resolution,
      credits: 5,
      estimatedCost: duration * 0.15 // Approximate Veo 3.1 Fast cost per second
    });
  };

  const confirmGenerateVideo = async () => {
    if (!videoConfirmModal) return;

    const { segmentId } = videoConfirmModal;
    setVideoConfirmModal(null);
    setIsGenerating(true);

    const segment = segments.find(s => s.id === segmentId);
    if (!segment) {
      setIsGenerating(false);
      return;
    }

    try {
      let videoPrompt = segment.metadata?.prompt || '';
      if (segment.metadata?.audioScript) {
        videoPrompt += `\n\nDialogue: "${segment.metadata.audioScript}"`;
      }

      // Using new object signature for generateBibleVideo with all Veo 3.1 parameters
      const videoData = await generateBibleVideo({
        prompt: videoPrompt,
        imageInput: segment.metadata?.imageUrl,
        style: (segment.metadata?.videoStyle as VideoStyle) || 'cinematic',
        resolution: (segment.metadata?.videoResolution as VideoResolution) || '1080p',
        duration: (segment.metadata?.videoDuration as VideoDuration) || 8,
        aspectRatio: (segment.metadata?.videoAspectRatio as VideoAspectRatio) || '16:9',
        negativePrompt: segment.metadata?.videoNegativePrompt
      });

      // Handle Base64 Data URI
      if (videoData.startsWith('data:')) {
        const res = await fetch(videoData);
        const blob = await res.blob();

        const timestamp = Date.now();
        const storagePath = `orgs/${orgId}/studies/${studyId}/videos/${segmentId}-${timestamp}.mp4`;
        const downloadURL = await uploadVideoToStorage(blob, storagePath);

        updateSegment(segmentId, { content: downloadURL });

        // Increment Quota
        await incrementAIQuota(orgId, userUid, 'veo-video', 'Video Generation', undefined, undefined, downloadURL, {
          storagePath,
          prompt: videoPrompt,
          sourceType: 'segment',
          associatedStudyId: studyId,
          segmentId
        });
        await refreshQuotaStatus();
      } else {
        // Handle remote URI
        updateSegment(segmentId, { content: videoData });
      }
    } catch (error) {
      console.error(error);
      alert("Failed to generate video. Ensure API access.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMagicRewrite = (segmentId: string) => {
    const segment = segments.find(s => s.id === segmentId);
    if (!segment || !segment.content) return;

    setTextMagicModal({
      show: true,
      segmentId,
      prompt: '',
      originalText: segment.content
    });
  };

  const handleMagicGenerate = async () => {
    if (!textMagicModal || !textMagicModal.prompt.trim()) return;

    setIsMagicGenerating(true);
    try {
      const result = await generateBibleText(textMagicModal.prompt, textMagicModal.originalText);
      setTextMagicModal(prev => prev ? { ...prev, generatedText: result } : null);
    } catch (error) {
      alert("Failed to generate text. Please try again.");
    } finally {
      setIsMagicGenerating(false);
    }
  };

  const handleMagicAccept = () => {
    if (!textMagicModal || !textMagicModal.generatedText) return;

    updateSegment(textMagicModal.segmentId, { content: textMagicModal.generatedText });
    setTextMagicModal(null);
  };

  const handleSave = async () => {
    if (!title.trim()) return alert("Please enter a title");
    if (segments.length === 0) return alert("Please add at least one segment");

    // Validate quizzes
    const invalidQuiz = segments.find(s => s.type === 'quiz' && (!s.content || s.metadata?.quizOptions?.some(o => !o)));
    if (invalidQuiz) return alert("Please complete all fields for Quiz segments.");

    // Recursively remove undefined values from segments to satisfy Firestore
    const cleanedSegments = removeUndefined(segments);

    setIsSaving(true);
    try {
      if (initialData) {
        // Update existing study
        const updateData: any = {
          title,
          description,
          visibility,
          segments: cleanedSegments,
          tags
        };

        // Only include optional fields if they have values
        if (coverImage) {
          updateData.coverImage = coverImage;
          updateData.coverImagePosition = coverImagePosition;
        }

        await updateStudy(initialData.id, updateData);
      } else {
        // Create new study
        const studyData: any = {
          orgId, // Link to Org
          authorId: userUid,
          authorName: userName,
          title,
          description,
          visibility,
          segments: cleanedSegments,
          createdAt: Date.now(),
          tags
        };

        // Only include optional fields if they have values
        if (coverImage) {
          studyData.coverImage = coverImage;
          studyData.coverImagePosition = coverImagePosition;
        }

        await saveStudy(studyData);
      }
      onSaveSuccess();
    } catch (error) {
      console.error(error);
      alert("Error saving study");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!initialData) return;
    setIsSaving(true); // Use saving state to show loading
    try {
      await deleteStudy(initialData.id);
      onSaveSuccess(); // Reuse success callback to return to dashboard
    } catch (error) {
      console.error(error);
      alert("Error deleting study");
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white min-h-screen pb-20 relative">
      {/* Header with Navigation */}
      <AppHeader
        user={user}
        organization={organization}
        onLogout={onLogout}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* Editor Action Bar */}
      <div className="sticky top-[73px] z-10 bg-white border-b px-3 md:px-6 py-3 md:py-4 shadow-sm">
        {/* Mobile Layout */}
        <div className="md:hidden space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-serif font-bold text-gray-800">
              {initialData ? 'Edit Study' : 'Create Study'}
            </h2>
            {aiQuotaStatus && (
              <AIQuotaDisplay
                used={aiQuotaStatus.images.used}
                limit={aiQuotaStatus.images.limit}
                remaining={aiQuotaStatus.images.remaining}
              />
            )}
          </div>
          <div className="flex gap-2">
            {initialData && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-100"
                title="Delete Study"
              >
                <Icons.Trash className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="flex-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-blue-900 transition-colors disabled:opacity-50"
            >
              {isSaving ? <Icons.Loader className="animate-spin w-4 h-4" /> : <Icons.Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-serif font-bold text-gray-800">
              {initialData ? 'Edit Study' : 'Create Study'}
            </h2>
            {aiQuotaStatus && (
              <AIQuotaDisplay
                used={aiQuotaStatus.images.used}
                limit={aiQuotaStatus.images.limit}
                remaining={aiQuotaStatus.images.remaining}
              />
            )}
          </div>
          <div className="flex gap-2">
            {initialData && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-100"
                title="Delete Study"
              >
                <Icons.Trash className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-900 transition-colors disabled:opacity-50"
            >
              {isSaving ? <Icons.Loader className="animate-spin w-4 h-4" /> : <Icons.Save className="w-4 h-4" />}
              Save Study
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto mt-4 md:mt-8 px-3 md:px-6 space-y-6 md:space-y-8">

        {/* Metadata */}
        <div className="space-y-4 p-4 md:p-6 bg-gray-50 rounded-xl border border-gray-100">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-lg font-serif"
              placeholder="e.g., The Parable of the Sower"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              rows={3}
              placeholder="A brief overview of the study..."
            />
          </div>

          {/* Cover Image Section */}
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">Study Cover Image</label>

            {coverImage ? (
              <div className="space-y-2">
                <div
                  className="relative group rounded-lg overflow-hidden h-40 w-full cursor-move select-none"
                  onMouseDown={handleCoverDragStart}
                  onMouseMove={handleCoverDragMove}
                  onMouseUp={handleCoverDragEnd}
                  onMouseLeave={handleCoverDragEnd}
                  style={{ touchAction: 'none' }}
                >
                  <img
                    src={coverImage}
                    alt="Cover"
                    className="w-full h-full object-cover pointer-events-none"
                    style={{
                      objectPosition: `${coverImagePosition.x}% ${coverImagePosition.y}%`
                    }}
                  />
                  <div className={`absolute inset-0 transition-opacity flex flex-col items-center justify-center gap-2 ${isDragging ? 'bg-black/30' : 'bg-black/50 opacity-0 group-hover:opacity-100'}`}>
                    {isDragging ? (
                      <div className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded">
                        Drag to reposition
                      </div>
                    ) : (
                      <>
                        <div className="text-white text-xs font-medium bg-black/50 px-3 py-1 rounded mb-2">
                          Click and drag to reposition
                        </div>
                        <button
                          onClick={() => setCoverImage('')}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="px-4 py-2 bg-white text-red-600 rounded-lg shadow-lg font-medium flex items-center gap-2 hover:bg-red-50"
                        >
                          <Icons.Trash className="w-4 h-4" /> Remove Cover
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Position: {Math.round(coverImagePosition.x)}%, {Math.round(coverImagePosition.y)}%</span>
                  <button
                    onClick={() => setCoverImagePosition({ x: 50, y: 50 })}
                    className="text-primary hover:underline"
                  >
                    Reset Position
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Icons.Image className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input
                      value={coverPrompt}
                      onChange={(e) => setCoverPrompt(e.target.value)}
                      placeholder={title ? `Prompt (defaults to title: "${title}")` : "Enter prompt for AI cover..."}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary focus:outline-none text-sm"
                    />
                  </div>
                  <button
                    onClick={handleGenerateCover}
                    disabled={isGeneratingCover || (!coverPrompt && !title)}
                    className="px-4 py-2 bg-secondary text-blue-900 font-medium rounded-lg hover:bg-yellow-400 disabled:opacity-50 flex items-center gap-2 text-sm whitespace-nowrap"
                  >
                    {isGeneratingCover ? <Icons.Loader className="w-4 h-4 animate-spin" /> : <Icons.Star className="w-4 h-4" />}
                    AI Generate
                  </button>
                </div>

                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="pointer-events-none">
                    <Icons.Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                    <span className="text-sm text-gray-500">Or click to upload a custom cover image</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tags Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Icons.Tag className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  placeholder="Add tags (press Enter)..."
                />
              </div>
              <button
                onClick={() => handleAddTag()}
                disabled={!tagInput.trim()}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {tags.map((tag, index) => (
                  <span key={index} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100">
                    #{tag}
                    <button onClick={() => handleRemoveTag(tag)} className="hover:text-blue-900 ml-1">
                      <Icons.X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as VisibilityLevel)}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value={VisibilityLevel.PRIVATE}>Private - Only you can see this study</option>
              <option value={VisibilityLevel.ORG_ONLY}>Organization - Members of {organization?.name || 'your org'} can see this study</option>
              <option value={VisibilityLevel.ALL_ORGS}>All Organizations - Other orgs can copy this study</option>
              <option value={VisibilityLevel.PUBLIC}>Public - Anyone on the internet can view this study</option>
            </select>
          </div>
        </div>

        {/* Segments */}
        <div className="space-y-4 md:space-y-6">
          {segments.map((segment, index) => (
            <div key={segment.id} className="relative group bg-white border-l-4 border-primary rounded-r-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="absolute -left-3 top-4 flex flex-col gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <button onClick={() => moveSegment(index, 'up')} className="p-1 bg-white border rounded shadow hover:bg-gray-50"><Icons.MoveUp className="w-3 h-3 text-gray-600" /></button>
                <button onClick={() => moveSegment(index, 'down')} className="p-1 bg-white border rounded shadow hover:bg-gray-50"><Icons.MoveDown className="w-3 h-3 text-gray-600" /></button>
                <button onClick={() => removeSegment(index)} className="p-1 bg-white border rounded shadow hover:bg-red-50"><Icons.Trash className="w-3 h-3 text-red-500" /></button>
              </div>

              <div className="p-4 md:p-6">
                <div className="flex items-center gap-2 mb-4 text-primary font-medium text-sm uppercase tracking-wide">
                  {segment.type === 'image-text' && (
                    <>
                      {(segment.metadata?.uiMode === 'text' || (!segment.metadata?.uiMode && !segment.metadata?.imageUrl)) ? (
                        <>
                          <Icons.Text className="w-4 h-4" />
                          <span>Text Segment</span>
                        </>
                      ) : (segment.metadata?.uiMode === 'image' || (!segment.metadata?.uiMode && segment.metadata?.imageUrl && !segment.content)) ? (
                        <>
                          <Icons.Image className="w-4 h-4" />
                          <span>Image Segment</span>
                        </>
                      ) : (
                        <>
                          <div className="flex gap-1">
                            <Icons.Image className="w-4 h-4" />
                            <Icons.Text className="w-4 h-4" />
                          </div>
                          <span>Content Segment</span>
                        </>
                      )}
                    </>
                  )}
                  {segment.type === 'video' && (
                    <>
                      <Icons.Youtube className="w-4 h-4" />
                      <span>Video Segment</span>
                    </>
                  )}
                  {segment.type === 'verse' && (
                    <>
                      <Icons.BookOpen className="w-4 h-4" />
                      <span>Verse Segment</span>
                    </>
                  )}
                  {segment.type === 'quiz' && (
                    <>
                      <Icons.Quiz className="w-4 h-4" />
                      <span>Quiz Segment</span>
                    </>
                  )}
                  {segment.type === 'question' && (
                    <>
                      <Icons.MessageSquare className="w-4 h-4" />
                      <span>Question Prompt</span>
                    </>
                  )}
                  {segment.type === 'cta' && (
                    <>
                      <Icons.Megaphone className="w-4 h-4" />
                      <span>Call To Action</span>
                    </>
                  )}
                </div>

                {/* Content Editors */}
                {segment.type === 'verse' && (
                  <div className="space-y-3">
                    {/* Bible Reference Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Book with Autocomplete */}
                      <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Book</label>
                        <input
                          value={segment.metadata?.book || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateSegmentMetadata(segment.id, { book: value });
                            setShowBookSuggestions({ ...showBookSuggestions, [segment.id]: value.length > 0 });
                          }}
                          onBlur={() => {
                            // Delay to allow clicking on suggestions
                            setTimeout(() => {
                              setShowBookSuggestions({ ...showBookSuggestions, [segment.id]: false });
                            }, 200);
                          }}
                          onFocus={(e) => {
                            if (e.target.value.length > 0) {
                              setShowBookSuggestions({ ...showBookSuggestions, [segment.id]: true });
                            }
                          }}
                          className="w-full p-2 border rounded focus:ring-1 focus:ring-primary"
                          placeholder="e.g., John"
                        />
                        {/* Autocomplete Suggestions */}
                        {showBookSuggestions[segment.id] && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {BIBLE_BOOKS
                              .filter(book =>
                                book.toLowerCase().includes((segment.metadata?.book || '').toLowerCase())
                              )
                              .map(book => (
                                <button
                                  key={book}
                                  type="button"
                                  onClick={() => {
                                    updateSegmentMetadata(segment.id, { book });
                                    setShowBookSuggestions({ ...showBookSuggestions, [segment.id]: false });
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                                >
                                  {book}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>

                      {/* Bible Version */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Version</label>
                        <select
                          value={segment.metadata?.version || 'NIV'}
                          onChange={(e) => updateSegmentMetadata(segment.id, { version: e.target.value })}
                          className="w-full p-2 border rounded focus:ring-1 focus:ring-primary"
                        >
                          {BIBLE_VERSIONS.map(version => (
                            <option key={version.id} value={version.id}>
                              {version.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Chapter */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Chapter</label>
                        <input
                          type="number"
                          min="1"
                          value={segment.metadata?.chapter || ''}
                          onChange={(e) => updateSegmentMetadata(segment.id, { chapter: e.target.value })}
                          className="w-full p-2 border rounded focus:ring-1 focus:ring-primary"
                          placeholder="e.g., 3"
                        />
                      </div>

                      {/* Verse(s) */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Verse(s)</label>
                        <input
                          value={segment.metadata?.verses || ''}
                          onChange={(e) => updateSegmentMetadata(segment.id, { verses: e.target.value })}
                          className="w-full p-2 border rounded focus:ring-1 focus:ring-primary"
                          placeholder="e.g., 16 or 16-17"
                        />
                      </div>
                    </div>

                    {/* Display formatted reference */}
                    {segment.metadata?.book && segment.metadata?.chapter && segment.metadata?.verses && (
                      <div className="text-sm font-semibold text-primary bg-blue-50 px-3 py-2 rounded">
                        {segment.metadata.book} {segment.metadata.chapter}:{segment.metadata.verses} ({segment.metadata.version || 'NIV'})
                      </div>
                    )}

                    {/* Copyright Warning */}
                    <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                      <Icons.AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>Ensure you have the right to use this Bible translation text.</span>
                    </div>

                    <textarea
                      value={segment.content}
                      onChange={(e) => updateSegment(segment.id, { content: e.target.value })}
                      className="w-full p-3 border rounded-lg italic font-serif bg-yellow-50/50"
                      placeholder="Paste the scripture text here..."
                    />

                    {/* Audio Generation Section */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-blue-900 uppercase">Professional Audio (Text to Speech)</label>
                        {segment.audioUrl && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <Icons.Check className="w-3 h-3" /> Generated
                          </span>
                        )}
                      </div>

                      {segment.audioUrl ? (
                        <div className="space-y-2">
                          <audio src={segment.audioUrl} controls className="w-full h-8" />
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>Voice: {segment.audioVoice?.replace('en-US-', '') || 'Unknown'}</span>
                            <button
                              onClick={() => handleDeleteAudio(segment.id)}
                              className="text-red-600 hover:text-red-700 flex items-center gap-1"
                            >
                              <Icons.Trash className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <select
                            value={selectedVoice}
                            onChange={(e) => setSelectedVoice(e.target.value)}
                            className="flex-1 px-2 py-1.5 text-sm border rounded"
                          >
                            {CHIRP_VOICES.map(voice => (
                              <option key={voice.name} value={voice.name}>
                                {voice.displayName}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleGenerateAudio(segment.id, segment.content)}
                            disabled={!segment.content || generatingAudioFor === segment.id}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                          >
                            {generatingAudioFor === segment.id ? (
                              <><Icons.Loader className="w-4 h-4 animate-spin" /> Generating...</>
                            ) : (
                              <><Icons.Audio className="w-4 h-4" /> Generate Audio</>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {segment.type === 'image-text' && (
                  <div className="space-y-6">
                    {/* UI Mode: Text Section */}
                    {/* Show if uiMode is 'text' or 'both', OR if it's undefined (legacy) AND (no image OR has content) */}
                    {(segment.metadata?.uiMode === 'text' || segment.metadata?.uiMode === 'both' || (!segment.metadata?.uiMode && (!segment.metadata?.imageUrl || segment.content))) && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icons.Text className="w-4 h-4 text-gray-500" />
                            <h4 className="font-semibold text-gray-700 text-sm">Text Content</h4>
                          </div>
                          {segment.metadata?.uiMode === 'both' && !segment.content && (
                            <button
                              onClick={() => updateSegmentMetadata(segment.id, { uiMode: 'image' })}
                              className="text-gray-400 hover:text-red-500"
                              title="Remove Text Section"
                            >
                              <Icons.X className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleMagicRewrite(segment.id)}
                            disabled={!segment.content}
                            className={`ml-2 flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${segment.content
                              ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300'
                              : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                              }`}
                            title="Reword with AI"
                          >
                            <Icons.Sparkles className="w-3 h-3" />
                            AI Rewrite
                          </button>
                        </div>
                        <div className="p-4 space-y-4">
                          <textarea
                            value={segment.content}
                            onChange={(e) => updateSegment(segment.id, { content: e.target.value })}
                            className="w-full p-3 border rounded-lg focus:ring-1 focus:ring-primary min-h-[100px]"
                            placeholder="Write your study content here..."
                          />

                          {/* Audio Generation - Only show if text is present */}
                          {segment.content && (
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 transition-all">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Icons.Audio className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm font-semibold text-blue-900">Audio Voiceover</span>
                                </div>
                                {segment.audioUrl && (
                                  <span className="text-xs font-medium text-green-700 flex items-center gap-1 bg-green-100 px-2 py-0.5 rounded-full">
                                    <Icons.Check className="w-3 h-3" /> Ready
                                  </span>
                                )}
                              </div>

                              {segment.audioUrl ? (
                                <div className="space-y-2">
                                  <audio src={segment.audioUrl} controls className="w-full h-8" />
                                  <div className="flex items-center justify-between text-xs text-gray-600">
                                    <span>Voice: {segment.audioVoice?.replace('en-US-', '') || 'Unknown'}</span>
                                    <button
                                      onClick={() => handleDeleteAudio(segment.id)}
                                      className="text-red-600 hover:text-red-700 text-xs font-medium flex items-center gap-1 hover:underline"
                                    >
                                      Delete Audio
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex flex-wrap items-center gap-3">
                                  <select
                                    value={selectedVoice}
                                    onChange={(e) => setSelectedVoice(e.target.value)}
                                    className="flex-1 min-w-[140px] px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                                  >
                                    {CHIRP_VOICES.map(voice => (
                                      <option key={voice.name} value={voice.name}>
                                        {voice.displayName}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => handleGenerateAudio(segment.id, segment.content)}
                                    disabled={!segment.content || generatingAudioFor === segment.id}
                                    className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 shadow-sm transition-colors whitespace-nowrap"
                                  >
                                    {generatingAudioFor === segment.id ? (
                                      <><Icons.Loader className="w-3 h-3 animate-spin" /> Generating...</>
                                    ) : (
                                      <><Icons.Audio className="w-3 h-3" /> Generate Audio</>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Add Text Button (if hidden) */}
                    {(segment.metadata?.uiMode === 'image' && !segment.content) && (
                      <button
                        onClick={() => updateSegmentMetadata(segment.id, { uiMode: 'both' })}
                        className="flex items-center gap-2 text-sm font-medium text-primary hover:text-blue-700 px-2"
                      >
                        <Icons.Plus className="w-4 h-4" /> Add Text
                      </button>
                    )}

                    {/* UI Mode: Image Section */}
                    {/* Show if uiMode is 'image' or 'both', OR if it's undefined (legacy) AND (has image OR no text) */}
                    {(segment.metadata?.uiMode === 'image' || segment.metadata?.uiMode === 'both' || (!segment.metadata?.uiMode && (segment.metadata?.imageUrl || !segment.content))) && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icons.Image className="w-4 h-4 text-gray-500" />
                            <h4 className="font-semibold text-gray-700 text-sm">Image</h4>
                          </div>
                          {segment.metadata?.uiMode === 'both' && !segment.metadata?.imageUrl && (
                            <button
                              onClick={() => updateSegmentMetadata(segment.id, { uiMode: 'text' })}
                              className="text-gray-400 hover:text-red-500"
                              title="Remove Image Section"
                            >
                              <Icons.X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="p-4 space-y-4">
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-col md:flex-row gap-2">
                              <input
                                value={segment.metadata?.prompt || ''}
                                onChange={(e) => updateSegmentMetadata(segment.id, { prompt: e.target.value })}
                                className="flex-1 p-2 border rounded-lg focus:ring-1 focus:ring-primary"
                                placeholder="Describe the image for AI generation..."
                              />
                              <div className="flex gap-2 shrink-0">
                                <button
                                  onClick={() => setShowSettings(prev => ({ ...prev, [segment.id]: !prev[segment.id] }))}
                                  className={`p-2 border rounded-lg hover:bg-gray-50 transition-colors ${showSettings[segment.id] ? 'bg-gray-100 border-gray-400' : 'bg-white border-gray-300'}`}
                                  title="Generation Settings"
                                >
                                  <Icons.Settings className="w-4 h-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={() => handleGenerateImage(segment.id)}
                                  disabled={isGenerating || !segment.metadata?.prompt}
                                  className="px-3 py-2 bg-secondary text-blue-900 font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 flex items-center gap-2 text-sm"
                                >
                                  {isGenerating ? <Icons.Loader className="w-4 h-4 animate-spin" /> : <Icons.Star className="w-4 h-4" />}
                                  AI Generate
                                </button>
                                <label className="px-3 py-2 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-sm border border-gray-300 shadow-sm">
                                  <Icons.Upload className="w-4 h-4" />
                                  Upload
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleImageUpload(segment.id, e)}
                                  />
                                </label>
                              </div>
                            </div>

                            {showSettings[segment.id] && (
                              <div className="flex flex-wrap gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm animate-fade-in">
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs font-semibold text-gray-500 uppercase">Style</label>
                                  <select
                                    value={segment.metadata?.imageStyle || 'cinematic'}
                                    onChange={(e) => updateSegmentMetadata(segment.id, { imageStyle: e.target.value })}
                                    className="p-1.5 border rounded-md bg-white focus:ring-1 focus:ring-primary min-w-[120px]"
                                  >
                                    {IMAGE_STYLES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                  </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs font-semibold text-gray-500 uppercase">Aspect Ratio</label>
                                  <select
                                    value={segment.metadata?.imageAspectRatio || '16:9'}
                                    onChange={(e) => updateSegmentMetadata(segment.id, { imageAspectRatio: e.target.value })}
                                    className="p-1.5 border rounded-md bg-white focus:ring-1 focus:ring-primary min-w-[120px]"
                                  >
                                    {ASPECT_RATIOS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>

                          {segment.metadata?.imageUrl ? (
                            <div className="relative group">
                              <img src={segment.metadata.imageUrl} alt={segment.metadata?.prompt || 'Study Image'} className="w-full rounded-lg shadow-md max-h-[300px] object-contain bg-white" />
                              <button
                                onClick={() => updateSegmentMetadata(segment.id, { imageUrl: '' })}
                                className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full shadow hover:bg-red-50 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove Image"
                              >
                                <Icons.X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-full h-24 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400">
                              <span className="text-xs">No image selected</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Add Image Button (if hidden) */}
                    {(segment.metadata?.uiMode === 'text' && !segment.metadata?.imageUrl) && (
                      <button
                        onClick={() => updateSegmentMetadata(segment.id, { uiMode: 'both' })}
                        className="flex items-center gap-2 text-sm font-medium text-primary hover:text-blue-700 px-2"
                      >
                        <Icons.Plus className="w-4 h-4" /> Add Image
                      </button>
                    )}
                  </div>
                )}

                {segment.type === 'video' && (
                  <div className="space-y-4">
                    {/* Video Source Tabs */}
                    <div className="flex border-b border-gray-200">
                      <button
                        className={`px-4 py-2 text-sm font-medium ${(!segment.metadata?.videoType || segment.metadata?.videoType === 'youtube') ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => updateSegmentMetadata(segment.id, { videoType: 'youtube' })}
                      >
                        YouTube
                      </button>
                      <button
                        className={`px-4 py-2 text-sm font-medium ${segment.metadata?.videoType === 'hosted' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => updateSegmentMetadata(segment.id, { videoType: 'hosted' })}
                      >
                        AI Generated Video
                      </button>
                    </div>

                    {/* YouTube Input */}
                    {(!segment.metadata?.videoType || segment.metadata?.videoType === 'youtube') && (
                      <div className="space-y-3">
                        <input
                          value={segment.content}
                          onChange={(e) => updateSegment(segment.id, { content: e.target.value })}
                          className="w-full p-2 border rounded"
                          placeholder="YouTube Video URL (e.g., https://youtu.be/...)"
                        />
                        {segment.content && (
                          <div className="aspect-video bg-gray-100 rounded overflow-hidden">
                            <iframe
                              src={`https://www.youtube.com/embed/${segment.content.split('v=')[1]?.split('&')[0] || segment.content.split('/').pop()}`}
                              className="w-full h-full"
                              title="Preview"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* AI Video Generator */}
                    {segment.metadata?.videoType === 'hosted' && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                          <div className="flex justify-between items-start mb-2">
                            <label className="block text-sm font-medium text-blue-900">Generate Video with Veo</label>
                          </div>

                          <div className="flex flex-wrap gap-4 mb-3 p-3 bg-white/60 rounded-lg border border-blue-100 text-sm animate-fade-in">
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-blue-800 uppercase">Style</label>
                              <select
                                value={segment.metadata?.videoStyle || 'cinematic'}
                                onChange={(e) => updateSegmentMetadata(segment.id, { videoStyle: e.target.value })}
                                className="p-1.5 border rounded-md bg-white focus:ring-1 focus:ring-primary min-w-[120px]"
                              >
                                {VIDEO_STYLES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                              </select>
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-blue-800 uppercase">Duration</label>
                              <select
                                value={segment.metadata?.videoDuration || 8}
                                onChange={(e) => updateSegmentMetadata(segment.id, { videoDuration: parseInt(e.target.value) })}
                                className="p-1.5 border rounded-md bg-white focus:ring-1 focus:ring-primary min-w-[140px]"
                              >
                                {VIDEO_DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                              </select>
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-blue-800 uppercase">Aspect Ratio</label>
                              <select
                                value={segment.metadata?.videoAspectRatio || '16:9'}
                                onChange={(e) => updateSegmentMetadata(segment.id, { videoAspectRatio: e.target.value })}
                                className="p-1.5 border rounded-md bg-white focus:ring-1 focus:ring-primary min-w-[130px]"
                              >
                                {VIDEO_ASPECT_RATIOS.map(ar => <option key={ar.value} value={ar.value}>{ar.label}</option>)}
                              </select>
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-blue-800 uppercase">Resolution</label>
                              <select
                                value={segment.metadata?.videoResolution || '1080p'}
                                onChange={(e) => updateSegmentMetadata(segment.id, { videoResolution: e.target.value })}
                                className="p-1.5 border rounded-md bg-white focus:ring-1 focus:ring-primary min-w-[140px]"
                                disabled={segment.metadata?.videoDuration !== 8 && segment.metadata?.videoResolution === '1080p'}
                              >
                                {VIDEO_RESOLUTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                              </select>
                              {segment.metadata?.videoDuration !== 8 && segment.metadata?.videoResolution === '1080p' && (
                                <span className="text-[10px] text-amber-600">1080p requires 8s duration</span>
                              )}
                            </div>
                          </div>

                          {/* Prompt Input */}
                          <textarea
                            value={segment.metadata?.prompt || ''}
                            onChange={(e) => updateSegmentMetadata(segment.id, { prompt: e.target.value })}
                            className="w-full p-3 border rounded-lg focus:ring-1 focus:ring-primary mb-3"
                            placeholder="Describe the video scene (e.g., 'A peaceful river flowing through a canyon at sunset, cinematic lighting')..."
                            rows={3}
                          />

                          {/* Audio Script Input */}
                          <div className="mb-3">
                            <label className="block text-xs font-semibold text-blue-800 uppercase mb-1">Audio Script (Optional)</label>
                            <textarea
                              value={segment.metadata?.audioScript || ''}
                              onChange={(e) => updateSegmentMetadata(segment.id, { audioScript: e.target.value })}
                              className="w-full p-3 border rounded-lg focus:ring-1 focus:ring-primary text-sm"
                              placeholder="Enter text to be spoken over the video..."
                              rows={2}
                            />
                          </div>

                          {/* Advanced Options - Collapsible */}
                          <details className="mb-3">
                            <summary className="cursor-pointer text-xs font-semibold text-gray-500 uppercase mb-2 hover:text-gray-700">
                              Advanced Options
                            </summary>
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Negative Prompt (Exclude Content)</label>
                                <input
                                  type="text"
                                  value={segment.metadata?.videoNegativePrompt || ''}
                                  onChange={(e) => updateSegmentMetadata(segment.id, { videoNegativePrompt: e.target.value })}
                                  className="w-full p-2 border rounded-lg focus:ring-1 focus:ring-primary text-sm"
                                  placeholder="e.g., blurry, distorted, low quality, modern objects..."
                                />
                              </div>
                            </div>
                          </details>

                          {/* Reference Image Input */}
                          <div className="mb-4">
                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Reference Image (Optional)</label>
                            <div className="flex items-center gap-3">
                              <label className="px-3 py-2 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-300 shadow-sm flex items-center gap-2 text-sm">
                                <Icons.Upload className="w-4 h-4" />
                                Upload Reference
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onload = async (ev) => {
                                        if (ev.target?.result) {
                                          const base64Data = ev.target.result as string;
                                          try {
                                            const timestamp = Date.now();
                                            const ext = file.name.split('.').pop() || 'png';
                                            const storagePath = `orgs/${orgId}/studies/${studyId}/images/${segment.id}-video-ref-${timestamp}.${ext}`;
                                            const downloadURL = await uploadImageToStorage(base64Data, storagePath);
                                            updateSegmentMetadata(segment.id, { imageUrl: downloadURL });
                                          } catch (error: any) {
                                            console.error("Failed to upload reference image", error);
                                            alert("Failed to upload reference image: " + error.message);
                                          }
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                              </label>
                              <button
                                onClick={() => setShowMediaPicker({ segmentId: segment.id, purpose: 'video-reference' })}
                                className="px-3 py-2 bg-blue-50 text-blue-700 font-medium rounded-lg hover:bg-blue-100 border border-blue-200 shadow-sm flex items-center gap-2 text-sm"
                              >
                                <Icons.Image className="w-4 h-4" />
                                Select from Library
                              </button>
                              {segment.metadata?.imageUrl && (
                                <div className="relative group w-16 h-16 rounded overflow-hidden border">
                                  <img src={segment.metadata.imageUrl} alt="Ref" className="w-full h-full object-cover" />
                                  <button
                                    onClick={() => updateSegmentMetadata(segment.id, { imageUrl: '' })}
                                    className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <Icons.X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          <button
                            onClick={() => handleGenerateVideo(segment.id)}
                            disabled={isGenerating || !segment.metadata?.prompt}
                            className="w-full py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-md"
                          >
                            {isGenerating ? (
                              <><Icons.Loader className="w-5 h-5 animate-spin" /> Generating Video...</>
                            ) : (
                              <><Icons.Youtube className="w-5 h-5" /> Generate 5s Video</>
                            )}
                          </button>
                        </div>

                        {/* Preview Generated Video */}
                        {segment.content && segment.metadata.videoType === 'hosted' && (
                          <div className="rounded-lg overflow-hidden border border-gray-200 bg-black">
                            <video src={segment.content} controls className="w-full max-h-[400px]" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {segment.type === 'quiz' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Question</label>
                      <input
                        value={segment.content}
                        onChange={(e) => updateSegment(segment.id, { content: e.target.value })}
                        className="w-full p-3 border rounded-lg font-medium"
                        placeholder="Enter your quiz question here..."
                      />
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-3">Options (Select the correct answer)</label>
                      <div className="space-y-2">
                        {segment.metadata?.quizOptions?.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center gap-3">
                            <input
                              type="radio"
                              name={`correct-answer-${segment.id}`}
                              checked={segment.metadata?.correctAnswer === optIndex}
                              onChange={() => updateSegmentMetadata(segment.id, { correctAnswer: optIndex })}
                              className="w-4 h-4 text-primary focus:ring-primary"
                            />
                            <div className="flex-1">
                              <input
                                value={option}
                                onChange={(e) => updateQuizOption(segment.id, optIndex, e.target.value)}
                                className={`w-full p-2 text-sm border rounded ${segment.metadata?.correctAnswer === optIndex ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                                placeholder={`Option ${optIndex + 1}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Audio Generation Section */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-blue-900 uppercase">Professional Audio (Text to Speech)</label>
                        {segment.audioUrl && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <Icons.Check className="w-3 h-3" /> Generated
                          </span>
                        )}
                      </div>

                      {segment.audioUrl ? (
                        <div className="space-y-2">
                          <audio src={segment.audioUrl} controls className="w-full h-8" />
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>Voice: {segment.audioVoice?.replace('en-US-', '') || 'Unknown'}</span>
                            <button
                              onClick={() => handleDeleteAudio(segment.id)}
                              className="text-red-600 hover:text-red-700 flex items-center gap-1"
                            >
                              <Icons.Trash className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <select
                            value={selectedVoice}
                            onChange={(e) => setSelectedVoice(e.target.value)}
                            className="flex-1 px-2 py-1.5 text-sm border rounded"
                          >
                            {CHIRP_VOICES.map(voice => (
                              <option key={voice.name} value={voice.name}>
                                {voice.displayName}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleGenerateAudio(segment.id, segment.content)}
                            disabled={!segment.content || generatingAudioFor === segment.id}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                          >
                            {generatingAudioFor === segment.id ? (
                              <><Icons.Loader className="w-4 h-4 animate-spin" /> Generating...</>
                            ) : (
                              <><Icons.Audio className="w-4 h-4" /> Generate Audio</>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {segment.type === 'question' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                        Prompt Text (shown to users)
                      </label>
                      <input
                        value={segment.metadata?.questionPrompt || ''}
                        onChange={(e) => updateSegmentMetadata(segment.id, { questionPrompt: e.target.value })}
                        className="w-full p-3 border rounded-lg font-medium"
                        placeholder="e.g., Have questions about this study? Ask them here!"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                        Placeholder Text
                      </label>
                      <input
                        value={segment.metadata?.questionPlaceholder || ''}
                        onChange={(e) => updateSegmentMetadata(segment.id, { questionPlaceholder: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="e.g., Type your question..."
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <label className="block text-xs font-semibold text-blue-900 uppercase mb-2">Preview</label>
                      <div className="bg-white rounded-lg p-4 border">
                        <p className="text-gray-800 font-medium mb-3">
                          {segment.metadata?.questionPrompt || 'Have questions?'}
                        </p>
                        <textarea
                          disabled
                          placeholder={segment.metadata?.questionPlaceholder || 'Ask your question...'}
                          className="w-full p-3 border rounded-lg bg-gray-50"
                          rows={3}
                        />
                        <div className="mt-2 flex justify-end">
                          <button disabled className="px-4 py-2 bg-gray-300 text-white rounded-lg">
                            Submit Question
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        ℹ️ Questions are private - only you can see them. Access via "Questions" tab.
                      </p>
                    </div>

                    <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                      <Icons.AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        Questions are stored separately and won't affect study content. View them in the Questions Manager.
                      </span>
                    </div>
                  </div>
                )}

                {segment.type === 'cta' && (
                  <div className="space-y-4">
                    {/* CTA Summary */}
                    {segment.metadata?.ctaType ? (
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {segment.metadata.ctaType === 'email_signup' && <Icons.Mail className="w-8 h-8 text-indigo-600" />}
                            {segment.metadata.ctaType === 'account_creation' && <Icons.UserPlus className="w-8 h-8 text-indigo-600" />}
                            {segment.metadata.ctaType === 'church_invite' && <Icons.Church className="w-8 h-8 text-indigo-600" />}
                            {segment.metadata.ctaType === 'free_book' && <Icons.Gift className="w-8 h-8 text-indigo-600" />}
                            {segment.metadata.ctaType === 'event_rsvp' && <Icons.Calendar className="w-8 h-8 text-indigo-600" />}
                            <div>
                              <h4 className="font-semibold text-gray-900">{segment.content || 'Call To Action'}</h4>
                              <p className="text-sm text-gray-600">
                                {segment.metadata.ctaType === 'email_signup' && 'Email Signup'}
                                {segment.metadata.ctaType === 'account_creation' && 'Account Creation'}
                                {segment.metadata.ctaType === 'church_invite' && 'Church Invite'}
                                {segment.metadata.ctaType === 'free_book' && 'Free Book Offer'}
                                {segment.metadata.ctaType === 'event_rsvp' && 'Event RSVP'}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setCtaEditorSegmentId(segment.id)}
                            className="px-3 py-1.5 text-sm bg-white border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50"
                          >
                            Edit CTA
                          </button>
                        </div>
                        {segment.metadata.ctaButtonText && (
                          <p className="mt-2 text-sm text-gray-500">Button: "{segment.metadata.ctaButtonText}"</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300 text-center">
                        <Icons.Megaphone className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h4 className="font-medium text-gray-700 mb-2">Configure Your Call To Action</h4>
                        <p className="text-sm text-gray-500 mb-4">
                          Add an interactive prompt to collect emails, RSVPs, or invite users to take action.
                        </p>
                        <button
                          onClick={() => setCtaEditorSegmentId(segment.id)}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                          Configure CTA
                        </button>
                      </div>
                    )}

                    <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                      <Icons.AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        CTAs appear as full-screen prompts during study viewing. Users can skip or complete them.
                      </span>
                    </div>
                  </div>
                )}

              </div>
            </div>
          ))}
        </div>

        {/* Add Segment Controls */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 pt-4 border-t">
          <button onClick={() => addSegment('image-text', { uiMode: 'text' })} className="flex flex-col items-center gap-2 p-3 md:p-4 border rounded-xl hover:bg-gray-50 hover:border-primary transition-colors">
            <Icons.Text className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            <span className="text-xs md:text-sm font-medium">Add Text</span>
          </button>
          <button onClick={() => addSegment('image-text', { uiMode: 'image' })} className="flex flex-col items-center gap-2 p-3 md:p-4 border rounded-xl hover:bg-gray-50 hover:border-primary transition-colors">
            <Icons.Image className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            <span className="text-xs md:text-sm font-medium">Add Image</span>
          </button>
          <button onClick={() => addSegment('image-text', { uiMode: 'both' })} className="flex flex-col items-center gap-2 p-3 md:p-4 border rounded-xl hover:bg-gray-50 hover:border-primary transition-colors">
            <div className="flex gap-1">
              <Icons.Text className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
              <Icons.Image className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
            </div>
            <span className="text-xs md:text-sm font-medium">Text & Image</span>
          </button>
          <button onClick={() => addSegment('verse')} className="flex flex-col items-center gap-2 p-3 md:p-4 border rounded-xl hover:bg-gray-50 hover:border-primary transition-colors">
            <Icons.BookOpen className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            <span className="text-xs md:text-sm font-medium">Add Verse</span>
          </button>
          <button onClick={() => addSegment('video')} className="flex flex-col items-center gap-2 p-3 md:p-4 border rounded-xl hover:bg-gray-50 hover:border-primary transition-colors">
            <Icons.Youtube className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            <span className="text-xs md:text-sm font-medium">Add Video</span>
          </button>
          <button onClick={() => addSegment('quiz')} className="flex flex-col items-center gap-2 p-3 md:p-4 border rounded-xl hover:bg-gray-50 hover:border-primary transition-colors">
            <Icons.Quiz className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            <span className="text-xs md:text-sm font-medium">Add Quiz</span>
          </button>
          <button onClick={() => addSegment('question', { questionPrompt: 'Have questions about this study?', questionPlaceholder: 'Ask your question here...' })} className="flex flex-col items-center gap-2 p-3 md:p-4 border rounded-xl hover:bg-gray-50 hover:border-primary transition-colors">
            <Icons.MessageSquare className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            <span className="text-xs md:text-sm font-medium">Add Question</span>
          </button>
          <button onClick={() => addSegment('cta')} className="flex flex-col items-center gap-2 p-3 md:p-4 border rounded-xl hover:bg-gray-50 hover:border-indigo-500 transition-colors">
            <Icons.Megaphone className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
            <span className="text-xs md:text-sm font-medium">Call to Action</span>
          </button>
        </div>

      </div>

      {/* Audio Generation Confirmation Modal */}
      {audioConfirmModal?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4">
          <div className="bg-white rounded-2xl p-4 md:p-6 max-w-md w-full shadow-2xl transform scale-100 animate-bounce-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Icons.Audio className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-serif font-bold text-gray-900">Generate Audio</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Generate professional text-to-speech audio for this segment?
            </p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Voice:</span>
                <span className="font-semibold text-gray-900">
                  {CHIRP_VOICES.find(v => v.name === audioConfirmModal.voice)?.displayName || audioConfirmModal.voice}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Duration:</span>
                <span className="font-semibold text-gray-900">
                  {Math.floor(audioConfirmModal.duration / 60)}:{(audioConfirmModal.duration % 60).toString().padStart(2, '0')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Est. Cost:</span>
                <span className="font-semibold text-gray-900">
                  ${audioConfirmModal.cost.toFixed(4)}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">AI Credits:</span>
                  <span className="font-semibold text-blue-600">1 credit</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setAudioConfirmModal(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmGenerateAudio}
                className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md flex items-center justify-center gap-2"
              >
                <Icons.Audio className="w-4 h-4" />
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Generation Confirmation Modal */}
      {imageConfirmModal?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4">
          <div className="bg-white rounded-2xl p-4 md:p-6 max-w-md w-full shadow-2xl transform scale-100 animate-bounce-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Icons.Image className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-serif font-bold text-gray-900">Generate Image</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Generate an AI image for this segment?
            </p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Style:</span>
                <span className="font-semibold text-gray-900 capitalize">
                  {imageConfirmModal.style}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Aspect Ratio:</span>
                <span className="font-semibold text-gray-900">
                  {imageConfirmModal.aspectRatio}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Est. Cost:</span>
                <span className="font-semibold text-gray-900">
                  ${imageConfirmModal.estimatedCost.toFixed(2)}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">AI Credits:</span>
                  <span className="font-semibold text-purple-600">{imageConfirmModal.credits} credit</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-100 rounded-lg p-3 mb-6">
              <p className="text-xs text-gray-600 line-clamp-3">
                <span className="font-semibold">Prompt:</span> {imageConfirmModal.prompt}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setImageConfirmModal(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmGenerateImage}
                className="flex-1 px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors shadow-md flex items-center justify-center gap-2"
              >
                <Icons.Image className="w-4 h-4" />
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Generation Confirmation Modal */}
      {videoConfirmModal?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4">
          <div className="bg-white rounded-2xl p-4 md:p-6 max-w-md w-full shadow-2xl transform scale-100 animate-bounce-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Icons.Youtube className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-serif font-bold text-gray-900">Generate Video</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Generate an AI video for this segment? This may take a few minutes.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Style:</span>
                <span className="font-semibold text-gray-900 capitalize">
                  {videoConfirmModal.style}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Duration:</span>
                <span className="font-semibold text-gray-900">
                  {videoConfirmModal.duration} seconds
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Resolution:</span>
                <span className="font-semibold text-gray-900">
                  {videoConfirmModal.resolution}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Est. Cost:</span>
                <span className="font-semibold text-gray-900">
                  ${videoConfirmModal.estimatedCost.toFixed(2)}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">AI Credits:</span>
                  <span className="font-semibold text-red-600">{videoConfirmModal.credits} credits</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-100 rounded-lg p-3 mb-6">
              <p className="text-xs text-gray-600 line-clamp-3">
                <span className="font-semibold">Prompt:</span> {videoConfirmModal.prompt}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setVideoConfirmModal(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmGenerateVideo}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-md flex items-center justify-center gap-2"
              >
                <Icons.Youtube className="w-4 h-4" />
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4">
          <div className="bg-white rounded-2xl p-4 md:p-6 max-w-sm w-full shadow-2xl transform scale-100 animate-bounce-in">
            <h3 className="text-lg md:text-xl font-serif font-bold text-gray-900 mb-2">Delete Study?</h3>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
              Are you sure you want to delete this study? This action cannot be undone.
            </p>
            <div className="flex gap-2 md:gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-3 md:px-4 py-2 text-sm md:text-base bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isSaving}
                className="flex-1 px-3 md:px-4 py-2 text-sm md:text-base bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-md disabled:opacity-50"
              >
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Picker Modal */}
      {showMediaPicker && (
        <MediaPicker
          user={user}
          organization={organization}
          allowedTypes={['image']}
          title="Select Reference Image"
          onSelect={(media: MediaItem) => {
            if (showMediaPicker.purpose === 'video-reference') {
              updateSegmentMetadata(showMediaPicker.segmentId, { imageUrl: media.url });
            }
            setShowMediaPicker(null);
          }}
          onClose={() => setShowMediaPicker(null)}
        />
      )}

      {/* CTA Editor Modal */}
      {ctaEditorSegmentId && (() => {
        const segment = segments.find(s => s.id === ctaEditorSegmentId);
        if (!segment) return null;

        // Convert segment metadata to CTAConfig format for the editor
        const existingCTA: CTAConfig | undefined = segment.metadata?.ctaType ? {
          id: segment.id,
          type: segment.metadata.ctaType,
          title: segment.content || '',
          description: undefined,
          buttonText: segment.metadata.ctaButtonText,
          skipText: segment.metadata.ctaSkipText,
          config: segment.metadata.ctaConfig || { type: segment.metadata.ctaType } as CTATypeConfig
        } : undefined;

        return (
          <CTAEditorModal
            existingCTA={existingCTA}
            onSave={(cta: CTAConfig) => {
              // Update segment with CTA config
              setSegments(prev => prev.map(s =>
                s.id === ctaEditorSegmentId
                  ? {
                    ...s,
                    content: cta.title,
                    metadata: {
                      ...s.metadata,
                      ctaType: cta.type,
                      ctaConfig: cta.config,
                      ctaButtonText: cta.buttonText,
                      ctaSkipText: cta.skipText
                    }
                  }
                  : s
              ));
              setCtaEditorSegmentId(null);
            }}
            onClose={() => setCtaEditorSegmentId(null)}
          />
        );
      })()}

      {/* Magic Text Modal */}
      {textMagicModal && textMagicModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl transform scale-100 animate-bounce-in">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-indigo-600">
                <Icons.Sparkles className="w-5 h-5" />
                <h3 className="text-xl font-serif font-bold text-gray-900">AI Rewrite</h3>
              </div>
              <button
                onClick={() => setTextMagicModal(null)}
                className="p-1 hover:bg-gray-100 rounded-full text-gray-500"
              >
                <Icons.X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What would you like to change?
              </label>
              <textarea
                value={textMagicModal.prompt}
                onChange={(e) => setTextMagicModal({ ...textMagicModal, prompt: e.target.value })}
                placeholder="e.g., Make it more poetic, simplify for children, explain the theological context..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[80px]"
              />
            </div>

            {textMagicModal.generatedText && (
              <div className="mb-6 bg-indigo-50 border border-indigo-100 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">Preview</span>
                  <button
                    onClick={() => {
                      if (navigator.clipboard) {
                        navigator.clipboard.writeText(textMagicModal.generatedText!);
                      }
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                  >
                    <Icons.Clipboard className="w-3 h-3" /> Copy
                  </button>
                </div>
                <div className="text-gray-800 text-sm italic whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                  "{textMagicModal.generatedText}"
                </div>
              </div>
            )}

            <div className="flex gap-3">
              {!textMagicModal.generatedText ? (
                <button
                  onClick={handleMagicGenerate}
                  disabled={isMagicGenerating || !textMagicModal.prompt.trim()}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2 shadow-lg shadow-indigo-200"
                >
                  {isMagicGenerating ? (
                    <>
                      <Icons.Loader className="w-5 h-5 animate-spin" />
                      Refining...
                    </>
                  ) : (
                    <>
                      <Icons.Sparkles className="w-5 h-5" />
                      Generate
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setTextMagicModal({ ...textMagicModal, generatedText: undefined })}
                    className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleMagicAccept}
                    className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow-md"
                  >
                    Use This Text
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
